import { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronLeft, ChevronRight, Plane } from 'lucide-react';

const statusColors = {
    scheduled: { bg: 'bg-slate-500/60', text: 'text-slate-100' },
    confirmed: { bg: 'bg-blue-500/70', text: 'text-blue-100' },
    preparing: { bg: 'bg-cyan-500/70', text: 'text-cyan-100' },
    boarding: { bg: 'bg-amber-500/70', text: 'text-amber-100' },
    closed: { bg: 'bg-orange-500/70', text: 'text-orange-100' },
    departed: { bg: 'bg-emerald-500/70', text: 'text-emerald-100' },
    en_route: { bg: 'bg-green-500/70', text: 'text-green-100' },
    arrived: { bg: 'bg-emerald-600/70', text: 'text-emerald-100' },
    delayed: { bg: 'bg-yellow-500/70', text: 'text-yellow-100' },
    cancelled: { bg: 'bg-red-500/60', text: 'text-red-100' },
};

const DAYS_ES_FULL = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
const DAYS_ES_SHORT = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
};

const formatDateKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getTimePosition = (timeStr) => {
    if (!timeStr) return null;
    const parts = timeStr.split('T')[1];
    if (!parts) return null;
    const [h, m] = parts.split(':').map(Number);
    return (h * 60 + m) / (24 * 60) * 100;
};

const getTimeDuration = (depStr, arrStr) => {
    const depPos = getTimePosition(depStr);
    const arrPos = getTimePosition(arrStr);
    if (depPos === null || arrPos === null) return { left: 0, width: 4 };
    let width = arrPos - depPos;
    if (width < 2) width = 2;
    if (width > 100 - depPos) width = 100 - depPos;
    return { left: depPos, width };
};

export const FlightTimelineView = ({ flights, onFlightClick }) => {
    const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));

    const weekDays = useMemo(() => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(weekStart);
            d.setDate(weekStart.getDate() + i);
            days.push(d);
        }
        return days;
    }, [weekStart]);

    const flightsByDate = useMemo(() => {
        const map = {};
        flights.forEach(f => {
            const dateStr = f.scheduled_departure?.split('T')[0];
            if (!dateStr) return;
            if (!map[dateStr]) map[dateStr] = [];
            map[dateStr].push(f);
        });
        Object.keys(map).forEach(k => {
            map[k].sort((a, b) => (a.scheduled_departure || '').localeCompare(b.scheduled_departure || ''));
        });
        return map;
    }, [flights]);

    const prevWeek = () => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() - 7);
        setWeekStart(d);
    };
    const nextWeek = () => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + 7);
        setWeekStart(d);
    };
    const goThisWeek = () => setWeekStart(getMonday(new Date()));

    const todayStr = new Date().toISOString().split('T')[0];

    const weekEndDate = new Date(weekStart);
    weekEndDate.setDate(weekStart.getDate() + 6);

    const formatRange = () => {
        const s = weekStart;
        const e = weekEndDate;
        const sMonth = s.toLocaleDateString('es-ES', { month: 'short' });
        const eMonth = e.toLocaleDateString('es-ES', { month: 'short' });
        if (sMonth === eMonth) {
            return `${s.getDate()} - ${e.getDate()} ${sMonth} ${s.getFullYear()}`;
        }
        return `${s.getDate()} ${sMonth} - ${e.getDate()} ${eMonth} ${s.getFullYear()}`;
    };

    return (
        <Card className="card-aviation" data-testid="flight-timeline-view">
            <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={prevWeek} data-testid="tl-prev-week">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-tight min-w-[240px] text-center" data-testid="tl-week-label">
                            {formatRange()}
                        </h2>
                        <Button variant="ghost" size="icon" onClick={nextWeek} data-testid="tl-next-week">
                            <ChevronRight className="w-5 h-5" />
                        </Button>
                    </div>
                    <Button variant="outline" size="sm" onClick={goThisWeek} data-testid="tl-this-week-btn">
                        Esta Semana
                    </Button>
                </div>

                {/* Timeline Grid */}
                <div className="overflow-x-auto">
                    <div className="min-w-[900px]">
                        {/* Hour headers */}
                        <div className="flex border-b border-border/30 ml-[140px]">
                            {HOURS.filter(h => h % 2 === 0).map(h => (
                                <div key={h} className="text-[10px] font-mono text-muted-foreground" style={{ width: `${100/12}%` }}>
                                    {String(h).padStart(2, '0')}:00
                                </div>
                            ))}
                        </div>

                        {/* Days */}
                        {weekDays.map((day, dayIdx) => {
                            const dateKey = formatDateKey(day);
                            const dayFlights = flightsByDate[dateKey] || [];
                            const isToday = dateKey === todayStr;
                            const dayName = DAYS_ES_SHORT[day.getDay()];

                            // Stack flights into lanes to avoid overlap
                            const lanes = [];
                            dayFlights.forEach(f => {
                                const { left, width } = getTimeDuration(f.scheduled_departure, f.scheduled_arrival);
                                const end = left + width;
                                let placed = false;
                                for (let l = 0; l < lanes.length; l++) {
                                    const lastEnd = lanes[l][lanes[l].length - 1].end;
                                    if (left >= lastEnd + 0.5) {
                                        lanes[l].push({ flight: f, left, width, end });
                                        placed = true;
                                        break;
                                    }
                                }
                                if (!placed) lanes.push([{ flight: f, left, width, end }]);
                            });

                            const rowHeight = Math.max(1, lanes.length) * 28 + 8;

                            return (
                                <div
                                    key={dayIdx}
                                    className={`flex border-b border-border/20 ${isToday ? 'bg-primary/5' : ''}`}
                                    data-testid={`tl-day-${dateKey}`}
                                >
                                    {/* Day label */}
                                    <div className={`w-[140px] flex-shrink-0 px-3 py-2 border-r border-border/20 ${isToday ? 'font-bold' : ''}`}>
                                        <div className="text-sm font-medium">{dayName}</div>
                                        <div className={`text-xs font-mono ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                                            {day.getDate()}/{day.getMonth() + 1}
                                        </div>
                                        {dayFlights.length > 0 && (
                                            <Badge variant="outline" className="text-[10px] mt-0.5 px-1 py-0">
                                                {dayFlights.length} vuelos
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Timeline area */}
                                    <div className="flex-1 relative" style={{ minHeight: `${rowHeight}px` }}>
                                        {/* Hour grid lines */}
                                        {HOURS.map(h => (
                                            <div
                                                key={h}
                                                className={`absolute top-0 bottom-0 border-l ${h % 6 === 0 ? 'border-border/30' : 'border-border/10'}`}
                                                style={{ left: `${(h / 24) * 100}%` }}
                                            />
                                        ))}

                                        {/* Current time indicator */}
                                        {isToday && (() => {
                                            const now = new Date();
                                            const pos = ((now.getHours() * 60 + now.getMinutes()) / (24 * 60)) * 100;
                                            return (
                                                <div
                                                    className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                                                    style={{ left: `${pos}%` }}
                                                    data-testid="tl-now-indicator"
                                                />
                                            );
                                        })()}

                                        {/* Flight bars */}
                                        {lanes.map((lane, laneIdx) =>
                                            lane.map(({ flight: f, left, width }) => {
                                                const colors = statusColors[f.status] || statusColors.scheduled;
                                                const depTime = f.scheduled_departure?.split('T')[1]?.slice(0, 5) || '';
                                                const arrTime = f.scheduled_arrival?.split('T')[1]?.slice(0, 5) || '';
                                                return (
                                                    <button
                                                        key={f.id}
                                                        onClick={() => onFlightClick?.(f)}
                                                        className={`absolute rounded-md px-1.5 flex items-center gap-1 text-[10px] font-medium cursor-pointer transition-all hover:brightness-125 hover:z-20 ${colors.bg} ${colors.text} border border-white/10`}
                                                        style={{
                                                            left: `${left}%`,
                                                            width: `${Math.max(width, 3)}%`,
                                                            top: `${laneIdx * 28 + 4}px`,
                                                            height: '24px',
                                                        }}
                                                        title={`${f.flight_number} | ${f.origin}-${f.destination} | ${depTime}-${arrTime}`}
                                                        data-testid={`tl-flight-${f.id}`}
                                                    >
                                                        <span className="font-bold font-mono truncate">{f.flight_number}</span>
                                                        {width > 6 && (
                                                            <span className="truncate opacity-80">{f.origin}-{f.destination}</span>
                                                        )}
                                                    </button>
                                                );
                                            })
                                        )}

                                        {/* Empty day message */}
                                        {dayFlights.length === 0 && (
                                            <div className="absolute inset-0 flex items-center justify-center text-[10px] text-muted-foreground/40">
                                                Sin vuelos
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Stats bar */}
                <div className="flex items-center gap-6 mt-3 pt-3 border-t border-border/30 text-xs text-muted-foreground">
                    <span>
                        <span className="font-bold text-foreground">
                            {weekDays.reduce((sum, d) => sum + (flightsByDate[formatDateKey(d)]?.length || 0), 0)}
                        </span> vuelos esta semana
                    </span>
                    <span className="flex items-center gap-1">
                        <div className="w-0.5 h-3 bg-red-500 rounded" /> Hora actual
                    </span>
                </div>
            </CardContent>
        </Card>
    );
};
